/**
 * SnaMetricsTab - Connection count, mention count, proxy metrics.
 * Centrality metrics show "Coming Soon" since they require Tier 2 API.
 */

import React from 'react';
import { Box, Typography, Divider, Chip, Alert } from '@mui/material';
import {
  Hub as HubIcon,
  ChatBubble as MentionIcon,
  Visibility as VisibilityIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { MESSAGES } from '../../constants/dashboardConfig';

function SnaMetricsTab({ node }) {
  const metrics = [
    {
      icon: <HubIcon sx={{ fontSize: 18 }} />,
      label: 'Connections (Degree)',
      value: node.degree ?? 0,
      available: true,
    },
    {
      icon: <MentionIcon sx={{ fontSize: 18 }} />,
      label: 'Mention Count',
      value: node.mention_count ?? 0,
      available: true,
    },
    {
      icon: <VisibilityIcon sx={{ fontSize: 18 }} />,
      label: 'Evidence Count',
      value: node.evidence_count ?? 0,
      available: true,
    },
    {
      icon: <CalendarIcon sx={{ fontSize: 18 }} />,
      label: 'First Seen',
      value: node.first_seen ? new Date(node.first_seen).toLocaleDateString() : 'N/A',
      available: true,
    },
    {
      icon: <CalendarIcon sx={{ fontSize: 18 }} />,
      label: 'Last Seen',
      value: node.last_seen ? new Date(node.last_seen).toLocaleDateString() : 'N/A',
      available: true,
    },
  ];

  const comingSoonMetrics = [
    'Betweenness Centrality',
    'Closeness Centrality',
    'Eigenvector Centrality',
    'Community Membership',
    'KPP Score',
  ];

  return (
    <Box sx={{ p: 2 }}>
      {/* Available Metrics */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
        Network Metrics
      </Typography>

      {metrics.map((metric) => (
        <Box
          key={metric.label}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 1,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box sx={{ color: 'primary.main' }}>{metric.icon}</Box>
          <Typography variant="body2" sx={{ flex: 1, fontSize: '0.85rem' }}>
            {metric.label}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {metric.value}
          </Typography>
        </Box>
      ))}

      <Divider sx={{ my: 2 }} />

      {/* Coming Soon - Centrality Metrics */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Advanced SNA Metrics
      </Typography>
      <Alert severity="info" sx={{ mb: 1.5, fontSize: '0.75rem' }}>
        Advanced metrics require the Tier 2 SNA Analytics API (graphology integration)
      </Alert>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {comingSoonMetrics.map((name) => (
          <Chip
            key={name}
            label={name}
            size="small"
            variant="outlined"
            sx={{
              fontSize: '0.7rem',
              height: 24,
              color: 'text.disabled',
              borderColor: 'divider',
            }}
          />
        ))}
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
        {MESSAGES.comingSoon}: Centrality metrics, community detection, and key player analysis
        will be available when the graphology-based analytics endpoint is implemented.
      </Typography>
    </Box>
  );
}

export default SnaMetricsTab;
